import { useTranslation } from '@govard/i18n-core/react';
import { useQuery } from '@tanstack/react-query';
import { Button, DatePicker, Form, Input, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';

import { ApiError } from '../../../shared/api-client/index.js';
import { listMembers } from '../../tasks/api/lookups.client.js';
import {
  reviseTaskProposalForm,
  type TaskProposalForm,
  type TaskProposalFormChange,
} from '../api/taskProposal.client.js';

interface FormFields {
  title: string;
  description: string;
  dueAt: Dayjs | null;
  priority: TaskProposalForm['fields']['priority'];
  assigneeMemberId: string;
}

function fieldsFromProposal(proposal: TaskProposalForm): FormFields {
  return {
    title: proposal.fields.title,
    description: proposal.fields.description ?? '',
    dueAt: proposal.fields.dueAt ? dayjs(proposal.fields.dueAt) : null,
    priority: proposal.fields.priority,
    assigneeMemberId: proposal.fields.assigneeMemberId ?? '',
  };
}

export function TaskProposalEditor({
  proposal,
  onSaved,
  onDirtyChange,
}: {
  proposal: TaskProposalForm;
  onSaved: (next: TaskProposalForm) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { t, locale } = useTranslation();
  const [form] = Form.useForm<FormFields>();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<'validation' | 'conflict' | 'other' | null>(null);
  const { data: members = [] } = useQuery({
    queryKey: ['task-proposal-members'],
    queryFn: listMembers,
    enabled: proposal.operation === 'create' && proposal.editable,
  });

  useEffect(() => {
    form.setFieldsValue(fieldsFromProposal(proposal));
    setDirty(false);
    setError(null);
    onDirtyChange(false);
  }, [form, onDirtyChange, proposal.revisionId]);

  async function save(values: FormFields) {
    if (!proposal.editable || !dirty) return;
    const original = fieldsFromProposal(proposal);
    const change: TaskProposalFormChange = {
      expectedDraftVersion: proposal.draftVersion,
      expectedRevisionId: proposal.revisionId,
    };
    if (values.title !== original.title) change.title = values.title;
    if (values.description !== original.description)
      change.description = values.description || null;
    if ((values.dueAt?.valueOf() ?? null) !== (original.dueAt?.valueOf() ?? null)) {
      if (values.dueAt && !values.dueAt.isValid()) {
        setError('validation');
        return;
      }
      change.dueAt = values.dueAt?.toISOString() ?? null;
    }
    if (values.priority !== original.priority) change.priority = values.priority;
    if (values.assigneeMemberId !== original.assigneeMemberId)
      change.assigneeMemberId = values.assigneeMemberId || null;
    setSaving(true);
    setError(null);
    try {
      const next = await reviseTaskProposalForm(proposal.approvalId ?? '', change);
      onSaved(next);
      setDirty(false);
      onDirtyChange(false);
    } catch (reason) {
      setError(reason instanceof ApiError && reason.status === 409 ? 'conflict' : 'other');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-lg border p-3" data-testid="task-proposal-editor">
      <p className="mb-2 text-sm font-medium">{t('crm.ai_chat.proposal.title')}</p>
      <p className="mb-3 text-xs text-muted-foreground">
        {t(
          proposal.operation === 'create'
            ? 'crm.ai_chat.proposal.create_effect'
            : 'crm.ai_chat.proposal.update_effect',
        )}
        {' · '}
        {t('crm.ai_chat.proposal.revision', { number: proposal.draftVersion })}
      </p>
      <Form<FormFields>
        form={form}
        layout="vertical"
        onFinish={(values) => void save(values)}
        onValuesChange={() => {
          const next = form.getFieldsValue();
          const changed = JSON.stringify(next) !== JSON.stringify(fieldsFromProposal(proposal));
          setDirty(changed);
          onDirtyChange(changed);
        }}
        disabled={!proposal.editable || saving}
      >
        <Form.Item
          name="title"
          label={t('crm.ai_chat.proposal.task_title')}
          rules={[{ required: true }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="description" label={t('crm.ai_chat.proposal.description')}>
          <Input.TextArea rows={2} maxLength={10_000} />
        </Form.Item>
        <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
          <Form.Item name="dueAt" label={t('crm.ai_chat.proposal.due_at')}>
            <DatePicker
              showTime
              format={locale === 'ru' ? 'DD.MM.YYYY HH:mm' : 'MM/DD/YYYY hh:mm A'}
              className="w-full"
            />
          </Form.Item>
          <Form.Item name="priority" label={t('crm.ai_chat.proposal.priority')}>
            <Select
              options={(['low', 'medium', 'high', 'urgent'] as const).map((value) => ({
                value,
                label: t(`crm.ai_chat.proposal.priority_${value}`),
              }))}
            />
          </Form.Item>
        </div>
        {proposal.operation === 'create' ? (
          <Form.Item name="assigneeMemberId" label={t('crm.ai_chat.proposal.assignee')}>
            <Select
              options={[
                { value: '', label: t('crm.ai_chat.proposal.unassigned') },
                ...members.map((member) => ({
                  value: member.id,
                  label: member.name || member.email,
                })),
              ]}
            />
          </Form.Item>
        ) : null}
        {!proposal.editable ? (
          <p role="status" className="mb-2 text-sm text-muted-foreground">
            {t(
              proposal.revisionStatus === 'editing'
                ? 'crm.ai_chat.proposal.saved_without_approval'
                : 'crm.ai_chat.proposal.read_only',
            )}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="mb-2 text-sm text-destructive">
            {t(
              error === 'conflict'
                ? 'crm.ai_chat.proposal.conflict'
                : error === 'validation'
                  ? 'crm.ai_chat.proposal.invalid_date'
                  : 'crm.ai_chat.proposal.save_error',
            )}
          </p>
        ) : null}
        {dirty ? (
          <p className="mb-2 text-xs text-muted-foreground">{t('crm.ai_chat.proposal.unsaved')}</p>
        ) : null}
        {proposal.editable ? (
          <Button htmlType="submit" loading={saving} disabled={!dirty || saving}>
            {t('crm.ai_chat.proposal.save')}
          </Button>
        ) : null}
      </Form>
    </div>
  );
}
